import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Dialog, DialogContent } from '../ui/dialog';
import { ShoppingCart, X, Layers, ListChecks, Plus, Minus } from 'lucide-react';
import { CATEGORY_LABELS, PRICING_MODEL_LABELS } from '../../utils/planningConstants';

export default function PackageDetailModal({ hub }) {
  const {
    viewingPackage, closePackageDetail, packageServiceList, packageOptions,
    removePackageService, updatePackageService, customizePackageService,
    getPackageLiveTotal, addPackageToCart, eventDetails
  } = hub;

  if (!viewingPackage) return null;

  return (
    <Dialog open={!!viewingPackage} onOpenChange={closePackageDetail}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="package-detail-modal">
        {viewingPackage.image_url && (
          <div className="h-40 sm:h-52 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 overflow-hidden relative rounded-t-lg">
            <img src={viewingPackage.image_url} alt={viewingPackage.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 sm:bottom-4 sm:left-6 sm:right-6">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2 flex-wrap">
                <Badge className="bg-indigo-600 text-white border-0 text-[10px] sm:text-xs"><Layers className="h-3 w-3 mr-1" /> Package</Badge>
                {viewingPackage.discount_type !== 'none' && (
                  <Badge className="bg-green-500 text-white border-0 text-[10px] sm:text-xs">
                    Save {viewingPackage.discount_type === 'percentage' ? `${viewingPackage.discount_value}%` : `$${viewingPackage.discount_value}`}
                  </Badge>
                )}
              </div>
              <h2 className="text-lg sm:text-2xl font-bold text-white leading-tight">{viewingPackage.name}</h2>
            </div>
          </div>
        )}
        {!viewingPackage.image_url && (
          <div className="mb-4">
            <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
              <Badge className="bg-indigo-600 text-white border-0 text-[10px] sm:text-xs"><Layers className="h-3 w-3 mr-1" /> Package</Badge>
              {viewingPackage.discount_type !== 'none' && (
                <Badge className="bg-green-500 text-white border-0 text-[10px] sm:text-xs">
                  Save {viewingPackage.discount_type === 'percentage' ? `${viewingPackage.discount_value}%` : `$${viewingPackage.discount_value}`}
                </Badge>
              )}
            </div>
            <h2 className="text-xl sm:text-2xl font-bold">{viewingPackage.name}</h2>
          </div>
        )}

        {viewingPackage.description && <p className="text-muted-foreground text-sm mb-2">{viewingPackage.description}</p>}

        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-500/10 to-purple-500/10 rounded-full border border-indigo-500/20">
            <ListChecks className="h-4 w-4 text-indigo-500" />
            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">{packageServiceList.length} Service{packageServiceList.length !== 1 && 's'}</span>
          </div>
          <div className="h-px flex-1 bg-gradient-to-r from-indigo-500/20 to-transparent" />
          <span className="text-xs text-muted-foreground">Customize each service below</span>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {packageServiceList.map(svc => {
            const savedOpts = packageOptions[svc.service_id] || {};
            const tierGroup = (svc.option_groups || []).find(g => g.name === 'Package Tier');
            const selectedTierId = tierGroup ? (savedOpts[tierGroup.option_id] || svc.default_tier || tierGroup.choices?.find(c => c.is_default)?.choice_id) : null;
            const selectedTierChoice = tierGroup?.choices?.find(c => c.choice_id === selectedTierId);
            const unitPrice = svc.base_price + (selectedTierChoice?.price_modifier || 0);
            const hasOptions = (svc.option_groups || []).length > 0;
            const optionCount = Object.keys(savedOpts).length;

            return (
              <Card key={svc.service_id} className="overflow-hidden group relative" data-testid={`pkg-service-${svc.service_id}`}>
                <button onClick={() => removePackageService(svc.service_id)} className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/50 hover:bg-red-500 text-white flex items-center justify-center transition-colors" data-testid={`pkg-remove-${svc.service_id}`}>
                  <X className="h-4 w-4" />
                </button>
                {svc.image_url && (
                  <div className="h-36 overflow-hidden relative">
                    <img src={svc.image_url} alt={svc.name} loading="lazy" className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 flex gap-1.5">
                      <Badge variant="secondary" className="text-xs">{CATEGORY_LABELS[svc.category] || svc.category}</Badge>
                      {optionCount > 0 && <Badge className="bg-indigo-500 text-white text-xs border-0">Customized</Badge>}
                    </div>
                  </div>
                )}
                <CardHeader className="pb-1 pt-3 px-4">
                  <CardTitle className="text-base leading-tight">{svc.name}</CardTitle>
                  {selectedTierChoice && <p className="text-xs text-indigo-500 font-medium">{selectedTierChoice.name}</p>}
                </CardHeader>
                <CardContent className="px-4 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <div><span className="text-lg font-bold text-indigo-500">${unitPrice.toFixed(2)}</span><span className="text-xs text-muted-foreground ml-1">/{PRICING_MODEL_LABELS[svc.pricing_model]?.toLowerCase() || 'unit'}</span></div>
                    {(svc.pricing_model === 'per_person' || svc.pricing_model === 'per_hour_per_person') && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => updatePackageService(svc.service_id, '_guestCount', Math.max(10, (svc._guestCount || 20) - 5))} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                        <span className="text-xs w-8 text-center font-medium">{svc._guestCount || 20}g</span>
                        <button onClick={() => updatePackageService(svc.service_id, '_guestCount', Math.min(500, (svc._guestCount || 20) + 5))} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                      </div>
                    )}
                    {(svc.pricing_model === 'per_hour') && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => updatePackageService(svc.service_id, '_hours', Math.max(1, (svc._hours || 2) - 1))} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                        <span className="text-xs w-8 text-center font-medium">{svc._hours || 2}hr</span>
                        <button onClick={() => updatePackageService(svc.service_id, '_hours', Math.min(12, (svc._hours || 2) + 1))} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                      </div>
                    )}
                    {(svc.pricing_model === 'flat_rate') && svc.category === 'staffing' && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => updatePackageService(svc.service_id, '_quantity', Math.max(1, (svc._quantity || 1) - 1))} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Minus className="h-3 w-3" /></button>
                        <span className="text-xs w-5 text-center font-medium">{svc._quantity || 1}</span>
                        <button onClick={() => updatePackageService(svc.service_id, '_quantity', (svc._quantity || 1) + 1)} className="w-6 h-6 rounded bg-muted hover:bg-muted-foreground/20 flex items-center justify-center text-xs"><Plus className="h-3 w-3" /></button>
                      </div>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="px-4 pt-1 pb-3">
                  {hasOptions ? (
                    <Button variant="outline" size="sm" className="w-full" onClick={() => customizePackageService(svc)} data-testid={`pkg-customize-${svc.service_id}`}>Customize & Configure</Button>
                  ) : (
                    <div className="w-full text-center text-xs text-muted-foreground py-1">No customizations available</div>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {packageServiceList.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Layers className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">All services removed from package</p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 flex flex-col items-center gap-4 p-4 bg-muted/50 rounded-lg border">
          <div className="w-full text-center sm:text-left">
            <p className="text-sm text-muted-foreground">Package Total</p>
            <div className="flex items-baseline gap-2 justify-center sm:justify-start">
              <span className="text-3xl font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">${getPackageLiveTotal().toFixed(0)}</span>
              {viewingPackage.discount_type !== 'none' && (
                <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-0">
                  {viewingPackage.discount_type === 'percentage' ? `${viewingPackage.discount_value}% off applied` : `$${viewingPackage.discount_value} off applied`}
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{packageServiceList.length} service{packageServiceList.length !== 1 && 's'} configured</p>
          </div>
          <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 h-12 text-base" onClick={addPackageToCart} disabled={packageServiceList.length === 0} data-testid="add-package-to-cart-btn">
            <ShoppingCart className="mr-2 h-5 w-5" /> Add Package to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
